<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      :title="settleAlertTitle"
      class="mb-12px"
    />
    <el-descriptions v-if="firmSettleEnabled && wallet" :column="4" border class="mb-12px" size="small">
      <el-descriptions-item label="可发放余额">
        ¥{{ Number(wallet.availableBalance ?? 0).toFixed(2) }}
      </el-descriptions-item>
      <el-descriptions-item label="累计入账">
        ¥{{ Number(wallet.totalIn ?? 0).toFixed(2) }}
      </el-descriptions-item>
      <el-descriptions-item label="累计发放">
        ¥{{ Number(wallet.totalOut ?? 0).toFixed(2) }}
      </el-descriptions-item>
      <el-descriptions-item label="留存比例">
        {{ Number(wallet.retainRate ?? 0) }}%
      </el-descriptions-item>
    </el-descriptions>
    <el-tabs v-model="tab" @tab-change="onTabChange">
      <el-tab-pane label="本所已付订单" name="orders" />
      <el-tab-pane v-if="firmSettleEnabled" label="结算申请" name="applies" />
    </el-tabs>

    <template v-if="tab === 'orders'">
      <el-form :inline="true" class="mb-12px" @submit.prevent>
        <el-form-item label="结算态">
          <el-select v-model="orderQuery.settleStatus" clearable placeholder="全部" class="!w-140px">
            <el-option label="未结算" value="UNSET" />
            <el-option label="已申请" value="APPLIED" />
            <el-option label="已入所" value="FIRM_CREDITED" />
            <el-option label="核对中" value="CHECKING" />
            <el-option label="已结算" value="SETTLED" />
            <el-option label="已驳回" value="REJECTED" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadOrders">搜索</el-button>
          <el-button
            v-if="firmSettleEnabled"
            type="success"
            :disabled="!selectedUnsetOrders.length"
            @click="openApplyFromSelection"
          >
            用勾选订单申请结算（{{ selectedUnsetOrders.length }}）
          </el-button>
        </el-form-item>
      </el-form>
      <el-table
        v-loading="orderLoading"
        :data="orderList"
        border
        row-key="id"
        @selection-change="onOrderSelectionChange"
      >
        <el-table-column
          v-if="firmSettleEnabled"
          type="selection"
          width="48"
          :selectable="canSelectOrder"
        />
        <el-table-column prop="id" label="编号" width="80" />
        <el-table-column prop="orderNo" label="订单号" min-width="140" />
        <el-table-column prop="lawyerId" label="律师" width="90" />
        <el-table-column prop="type" label="类型" width="120" />
        <el-table-column prop="payAmount" label="支付额" width="100" />
        <el-table-column prop="commAmount" label="平台佣金" width="100" />
        <el-table-column prop="lawyerAmount" label="所侧应收" width="100" />
        <el-table-column prop="settleStatus" label="结算态" width="100" />
        <el-table-column prop="firmSettleApplyId" label="结算申请" width="100" />
        <el-table-column prop="createTime" label="创建时间" width="170" :formatter="dateFormatter" />
      </el-table>
      <Pagination
        v-model:page="orderQuery.pageNo"
        v-model:limit="orderQuery.pageSize"
        :total="orderTotal"
        @pagination="loadOrders"
      />
    </template>

    <template v-else>
      <div class="mb-12px">
        <el-button v-if="firmSettleEnabled" type="primary" @click="openApplyAllUnset">
          发起结算申请（全部未结）
        </el-button>
        <el-alert
          v-else
          type="warning"
          :closable="false"
          show-icon
          class="mb-8px"
          title="当前经营模式未开启律所结算申请；订单仍可查看。"
        />
        <el-select
          v-if="firmSettleEnabled"
          v-model="applyQuery.status"
          clearable
          placeholder="状态"
          class="!w-140px ml-12px"
          @change="loadApplies"
        >
          <el-option label="待审" value="PENDING" />
          <el-option label="已通过" value="APPROVED" />
          <el-option label="已驳回" value="REJECTED" />
          <el-option label="已打款" value="PAID" />
        </el-select>
      </div>
      <el-table v-loading="applyLoading" :data="applyList" border>
        <el-table-column prop="id" label="编号" width="80" />
        <el-table-column prop="applyNo" label="申请单号" min-width="140" />
        <el-table-column prop="amount" label="金额" width="100" />
        <el-table-column prop="orderCount" label="订单数" width="90" />
        <el-table-column label="周期" min-width="180">
          <template #default="{ row }">
            {{ row.periodStart || '—' }} ~ {{ row.periodEnd || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="applyTagType(row.status)" size="small">{{ applyLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip />
        <el-table-column prop="auditRemark" label="平台备注" min-width="120" show-overflow-tooltip />
        <el-table-column prop="createTime" label="申请时间" width="170" :formatter="dateFormatter" />
        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <el-button
              v-if="row.status === 'APPROVED' || row.status === 'PAID'"
              link
              type="success"
              :loading="payoutId === row.id"
              @click="onPayout(row)"
            >
              发放律师
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <Pagination
        v-model:page="applyQuery.pageNo"
        v-model:limit="applyQuery.pageSize"
        :total="applyTotal"
        @pagination="loadApplies"
      />
    </template>

    <Dialog v-model="applyVisible" title="发起结算申请" width="520px">
      <el-alert
        type="info"
        :closable="false"
        class="mb-12px"
        :title="
          applyForm.orderIds?.length
            ? `将锁定 ${applyForm.orderIds.length} 笔未结订单，金额按「所侧应收」汇总（审核通过后入所账户，待发放律师）。`
            : '将纳入本所全部已付未结订单（可按周期过滤），金额按「所侧应收」自动汇总。'
        "
      />
      <el-form ref="applyFormRef" :model="applyForm" label-width="100px">
        <el-form-item v-if="applyForm.orderIds?.length" label="已选订单">
          <span>{{ applyForm.orderIds.join(', ') }}</span>
        </el-form-item>
        <el-form-item label="周期起">
          <el-date-picker
            v-model="applyForm.periodStart"
            type="date"
            value-format="YYYY-MM-DD"
            class="!w-100%"
            :disabled="!!applyForm.orderIds?.length"
          />
        </el-form-item>
        <el-form-item label="周期止">
          <el-date-picker
            v-model="applyForm.periodEnd"
            type="date"
            value-format="YYYY-MM-DD"
            class="!w-100%"
            :disabled="!!applyForm.orderIds?.length"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="applyForm.remark" type="textarea" :rows="2" maxlength="500" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="applyVisible = false">取消</el-button>
        <el-button type="primary" :loading="applySaving" @click="submitApply">提交</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import type { FormInstance } from 'element-plus'
import {
  FirmPortalApi,
  type FirmSettleApplyVO,
  type FirmSettleOrderVO,
  type FirmSettleWalletVO
} from '@/api/firm/portal'
import { getFirmWorkbenchContext } from '@/api/firm/auth'
import { dateFormatter } from '@/utils/formatTime'

defineOptions({ name: 'FirmPortalSettle' })

const firmSettleEnabled = ref(true)
const wallet = ref<FirmSettleWalletVO | null>(null)
const payoutId = ref<number | null>(null)
const settleAlertTitle = computed(() =>
  firmSettleEnabled.value
    ? '平台→所→律师（W6.1）：申请→审过入所账户→点「发放律师」进承办律师余额（按所留存比例扣留）；「已打款」为一期对公 mock。'
    : '本所已付订单为应收可视；当前经营模式未开启「结算申请」。'
)

const message = useMessage()
const route = useRoute()
const tab = ref<'orders' | 'applies'>('orders')

const orderLoading = ref(false)
const orderList = ref<FirmSettleOrderVO[]>([])
const orderTotal = ref(0)
const selectedOrders = ref<FirmSettleOrderVO[]>([])
const orderQuery = reactive({
  pageNo: 1,
  pageSize: 10,
  settleStatus: undefined as string | undefined
})

const selectedUnsetOrders = computed(() =>
  selectedOrders.value.filter((o) => o.settleStatus === 'UNSET' && !o.firmSettleApplyId)
)

const applyLoading = ref(false)
const applyList = ref<FirmSettleApplyVO[]>([])
const applyTotal = ref(0)
const applyQuery = reactive({
  pageNo: 1,
  pageSize: 10,
  status: undefined as string | undefined
})

const applyVisible = ref(false)
const applySaving = ref(false)
const applyFormRef = ref<FormInstance>()
const applyForm = reactive({
  orderIds: undefined as number[] | undefined,
  periodStart: undefined as string | undefined,
  periodEnd: undefined as string | undefined,
  remark: ''
})

const canSelectOrder = (row: FirmSettleOrderVO) =>
  row.settleStatus === 'UNSET' && !row.firmSettleApplyId

const onOrderSelectionChange = (rows: FirmSettleOrderVO[]) => {
  selectedOrders.value = rows
}

const applyLabel = (s?: string) =>
  ({ PENDING: '待审', APPROVED: '已通过', REJECTED: '已驳回', PAID: '已打款' }[s || ''] || s || '—')

const applyTagType = (s?: string) => {
  if (s === 'PAID' || s === 'APPROVED') return 'success'
  if (s === 'PENDING') return 'warning'
  if (s === 'REJECTED') return 'danger'
  return 'info'
}

const loadOrders = async () => {
  orderLoading.value = true
  selectedOrders.value = []
  try {
    const data = await FirmPortalApi.settleOrderPage({ ...orderQuery })
    orderList.value = data?.list || []
    orderTotal.value = data?.total || 0
  } finally {
    orderLoading.value = false
  }
}

const loadApplies = async () => {
  applyLoading.value = true
  try {
    const data = await FirmPortalApi.settleApplyPage({ ...applyQuery })
    applyList.value = data?.list || []
    applyTotal.value = data?.total || 0
  } finally {
    applyLoading.value = false
  }
}

const loadWallet = async () => {
  if (!firmSettleEnabled.value) {
    wallet.value = null
    return
  }
  try {
    wallet.value = await FirmPortalApi.settleWallet()
  } catch {
    wallet.value = null
  }
}

const onPayout = async (row: FirmSettleApplyVO) => {
  if (!row.id) return
  try {
    await message.confirm(
      `确认将申请 ${row.applyNo || row.id} 下「已入所」订单发放至承办律师？所留存比例按本所配置执行。`
    )
  } catch {
    return
  }
  payoutId.value = row.id
  try {
    const res = await FirmPortalApi.settlePayout({ applyId: row.id })
    message.success(`发放完成：新发 ${res?.paidCount ?? 0} 笔，跳过 ${res?.skippedCount ?? 0} 笔`)
    await Promise.all([loadApplies(), loadWallet(), loadOrders()])
  } finally {
    payoutId.value = null
  }
}

const onTabChange = () => {
  if (tab.value === 'orders') loadOrders()
  else loadApplies()
}

const resetApplyForm = () => {
  applyForm.orderIds = undefined
  applyForm.periodStart = undefined
  applyForm.periodEnd = undefined
  applyForm.remark = ''
}

const openApplyFromSelection = () => {
  if (!selectedUnsetOrders.value.length) {
    message.warning('请勾选未结算订单')
    return
  }
  resetApplyForm()
  applyForm.orderIds = selectedUnsetOrders.value.map((o) => o.id)
  applyVisible.value = true
}

const openApplyAllUnset = () => {
  resetApplyForm()
  applyVisible.value = true
}

const submitApply = async () => {
  applySaving.value = true
  try {
    const id = await FirmPortalApi.settleApply({
      orderIds: applyForm.orderIds,
      periodStart: applyForm.periodStart,
      periodEnd: applyForm.periodEnd,
      remark: applyForm.remark || undefined
    })
    message.success(`已提交结算申请（#${id}）`)
    applyVisible.value = false
    selectedOrders.value = []
    tab.value = 'applies'
    applyQuery.pageNo = 1
    await loadApplies()
  } finally {
    applySaving.value = false
  }
}

onMounted(async () => {
  try {
    const ctx = await getFirmWorkbenchContext()
    firmSettleEnabled.value = ctx?.firmSettleEnabled !== false
  } catch {
    firmSettleEnabled.value = true
  }
  await loadWallet()
  if (!firmSettleEnabled.value) {
    tab.value = 'orders'
    await loadOrders()
    return
  }
  if (route.query.tab === 'applies') {
    tab.value = 'applies'
    applyQuery.status = 'PENDING'
    await loadApplies()
    return
  }
  await loadOrders()
})
</script>
